/**
 * Returns the given URL if it is a safe relative redirect target or an absolute
 * URL whose origin is explicitly trusted, and returns the fallback otherwise.
 *
 * A URL is considered safe when it is a **relative** path that starts with a
 * single `/` (i.e. `/about`, `/posts/my-post`). The following categories are
 * rejected because they can be (or are commonly) abused for open-redirect or
 * tabnabbing attacks:
 *
 * - **Protocol-relative URLs** — e.g. `//evil.com`, which browsers resolve
 *   against the current protocol and navigate away from the origin.
 * - **Untrusted absolute URLs** — e.g. `https://evil.com`.
 * - **Non-http(s) schemes** — e.g. `javascript:`, `data:`, `mailto:`.
 *
 * @param url      The redirect target to validate.
 * @param fallback Value returned when `url` is not a safe relative path.
 * @param allowedOrigins Exact origins permitted for cross-subdomain navigation.
 * @returns The validated relative URL, or the fallback.
 */
export function getSafeRedirectUrl(
  url: string | null | undefined,
  fallback: string | null = null,
  allowedOrigins: readonly string[] = [],
): string | null {
  if (!url || typeof url !== 'string') {
    return fallback
  }

  const trimmed = url.trim()

  if (!trimmed) {
    return fallback
  }

  // Browsers and URL parsers normalize backslashes inconsistently, and control
  // characters can create ambiguous navigation/header values. Fail closed.
  if (trimmed.includes('\\') || /[\u0000-\u001F\u007F]/.test(trimmed)) {
    return fallback
  }

  // Only allow same-origin relative paths.
  // A leading single `/` is required; reject protocol-relative `//evil.com`.
  if (trimmed.startsWith('/') && !trimmed.startsWith('//')) {
    return trimmed
  }

  try {
    const parsed = new URL(trimmed)
    const trustedOrigins = new Set(
      allowedOrigins.flatMap((origin) => {
        try {
          return [new URL(origin).origin]
        } catch {
          return []
        }
      }),
    )

    if (
      ['http:', 'https:'].includes(parsed.protocol) &&
      !parsed.username &&
      !parsed.password &&
      trustedOrigins.has(parsed.origin)
    ) {
      return parsed.toString()
    }
  } catch {
    // Invalid and non-URL values fail closed below.
  }

  // Reject every other form: untrusted absolute URLs, protocol-relative URLs,
  // and non-http(s) schemes such as `javascript:`.
  return fallback
}
